import { createTheme } from "@mui/material/styles";

// Colori estratti dal sito Fracomina - Sellout
const fracominaSelloutColors = {
  primary: {
    main: "#434a54",
    light: "#303030",
    dark: "#2d313a",
    contrastText: "#ffffff",
  },
  secondary: {
    main: "#434a54",
    light: "#303030",
    dark: "#2d313a",
    contrastText: "#ffffff",
  },
  background: {
    default: "#f1f1f1",
    paper: "#ffffff",
    navbar: "#e9e3d9",
  },
  text: {
    primary: "#303030",
    secondary: "#999999",
    disabled: "#cccccc",
  },
  divider: "#efefef",
  success: {
    main: "#333333",
  },
  warning: {
    main: "#ff9800",
  },
  error: {
    main: "#f44336",
  },
  info: {
    main: "#48cfad",
  },
};

// Tema Material-UI personalizzato per Fracomina - Sellout
export const fracominaSelloutTheme = createTheme({
  palette: {
    mode: "light",
    primary: {
      main: fracominaSelloutColors.primary.main,
      light: fracominaSelloutColors.primary.light,
      dark: fracominaSelloutColors.primary.dark,
      contrastText: fracominaSelloutColors.primary.contrastText,
    },
    secondary: {
      main: fracominaSelloutColors.secondary.main,
      light: fracominaSelloutColors.secondary.light,
      dark: fracominaSelloutColors.secondary.dark,
      contrastText: fracominaSelloutColors.secondary.contrastText,
    },
    background: {
      default: fracominaSelloutColors.background.default,
      paper: fracominaSelloutColors.background.paper,
    },
    text: {
      primary: fracominaSelloutColors.text.primary,
      secondary: fracominaSelloutColors.text.secondary,
      disabled: fracominaSelloutColors.text.disabled,
    },
    divider: fracominaSelloutColors.divider,
    success: {
      main: fracominaSelloutColors.success.main,
    },
    warning: {
      main: fracominaSelloutColors.warning.main,
    },
    error: {
      main: fracominaSelloutColors.error.main,
    },
    info: {
      main: fracominaSelloutColors.info.main,
    },
  },
  typography: {
    fontFamily: "'Inter', 'Roboto', 'Helvetica', 'Arial', sans-serif",
    h1: {
      fontFamily: "'Inter', sans-serif",
      fontWeight: 800,
      fontSize: "2.5rem",
      lineHeight: 1.2,
      color: fracominaSelloutColors.text.primary,
      textTransform: "uppercase",
    },
    h2: {
      fontFamily: "'Inter', sans-serif",
      fontWeight: 700,
      fontSize: "2rem",
      lineHeight: 1.3,
      color: fracominaSelloutColors.text.primary,
      textTransform: "uppercase",
    },
    h3: {
      fontFamily: "'Inter', sans-serif",
      fontWeight: 700,
      fontSize: "1.75rem",
      lineHeight: 1.4,
      color: fracominaSelloutColors.text.primary,
    },
    h4: {
      fontFamily: "'Inter', sans-serif",
      fontWeight: 700,
      fontSize: "1.5rem",
      lineHeight: 1.4,
      color: fracominaSelloutColors.text.primary,
    },
    h5: {
      fontFamily: "'Inter', sans-serif",
      fontWeight: 600,
      fontSize: "1.25rem",
      lineHeight: 1.5,
      color: fracominaSelloutColors.text.primary,
    },
    h6: {
      fontFamily: "'Inter', sans-serif",
      fontWeight: 600,
      fontSize: "1rem",
      lineHeight: 1.5,
      color: fracominaSelloutColors.text.primary,
    },
    subtitle1: {
      fontFamily: "'Inter', sans-serif",
      fontWeight: 600,
      fontSize: "1rem",
      lineHeight: 1.75,
      color: fracominaSelloutColors.text.primary,
    },
    subtitle2: {
      fontFamily: "'Inter', sans-serif",
      fontWeight: 500,
      fontSize: "0.875rem",
      lineHeight: 1.57,
      color: fracominaSelloutColors.text.secondary,
    },
    body1: {
      fontFamily: "'Inter', sans-serif",
      fontWeight: 400,
      fontSize: "1rem",
      lineHeight: 1.5,
      color: fracominaSelloutColors.text.primary,
    },
    body2: {
      fontFamily: "'Inter', sans-serif",
      fontWeight: 400,
      fontSize: "0.875rem",
      lineHeight: 1.43,
      color: fracominaSelloutColors.text.primary,
    },
    button: {
      fontFamily: "'Inter', sans-serif",
      fontWeight: 700,
      fontSize: "0.875rem",
      lineHeight: 1.75,
      textTransform: "uppercase",
    },
    caption: {
      fontFamily: "'Inter', sans-serif",
      fontWeight: 400,
      fontSize: "0.75rem",
      lineHeight: 1.66,
      color: fracominaSelloutColors.text.secondary,
    },
    overline: {
      fontFamily: "'Inter', sans-serif",
      fontWeight: 600,
      fontSize: "0.75rem",
      lineHeight: 2.66,
      textTransform: "uppercase",
      color: fracominaSelloutColors.text.secondary,
    },
  },
  shape: {
    borderRadius: 3, // Simile al design del sito originale
  },
  components: {
    // Personalizzazione dei componenti Material-UI
    MuiButton: {
      styleOverrides: {
        root: {
          borderRadius: 3,
          fontWeight: 700,
          textTransform: "uppercase",
          transition: "all 0.3s ease-in-out",
        },
        contained: {
          backgroundColor: fracominaSelloutColors.primary.main,
          color: "#ffffff",
          "&:hover": {
            backgroundColor: fracominaSelloutColors.primary.dark,
            transform: "translateY(-1px)",
            boxShadow: "0 4px 8px rgba(55, 188, 155, 0.3)",
          },
        },
        outlined: {
          borderColor: fracominaSelloutColors.primary.main,
          color: fracominaSelloutColors.primary.main,
          "&:hover": {
            backgroundColor: fracominaSelloutColors.primary.main,
            color: "#ffffff",
            borderColor: fracominaSelloutColors.primary.main,
          },
        },
      },
    },
    MuiAppBar: {
      styleOverrides: {
        root: {
          backgroundColor: fracominaSelloutColors.background.navbar,
          color: fracominaSelloutColors.text.primary,
          boxShadow: "0 1px 3px rgba(0,0,0,.25)",
        },
      },
    },
    MuiCard: {
      styleOverrides: {
        root: {
          borderRadius: 3,
          boxShadow: "0 0 1px 1px #ddd",
          transition: "all 0.3s ease-in-out",
          "&:hover": {
            transform: "translateY(-2px)",
            boxShadow: "0 4px 12px rgba(0,0,0,0.15)",
          },
        },
      },
    },
    MuiPaper: {
      styleOverrides: {
        root: {
          borderRadius: 3,
        },
      },
    },
    MuiTextField: {
      styleOverrides: {
        root: {
          "& .MuiInputLabel-root": {
            fontWeight: 450,
          },
          "& .MuiOutlinedInput-root": {
            borderRadius: 7,
            "&:hover fieldset": {
              borderColor: fracominaSelloutColors.primary.light,
            },
            "&.Mui-focused fieldset": {
              borderColor: fracominaSelloutColors.primary.main,
            },
          },
        },
      },
    },
    MuiChip: {
      styleOverrides: {
        root: {
          borderRadius: 3,
          fontFamily: "'Inter', sans-serif",
          fontWeight: 600,
        },
        filled: {
          backgroundColor: fracominaSelloutColors.primary.main,
          color: "#ffffff",
          "&:hover": {
            backgroundColor: fracominaSelloutColors.primary.dark,
          },
        },
      },
    },
    MuiTabs: {
      styleOverrides: {
        indicator: {
          backgroundColor: fracominaSelloutColors.primary.main,
          height: 3,
        },
      },
    },
    MuiTab: {
      styleOverrides: {
        root: {
          fontFamily: "'Inter', sans-serif",
          fontWeight: 700,
          textTransform: "uppercase",
          color: fracominaSelloutColors.text.primary,
          "&.Mui-selected": {
            color: fracominaSelloutColors.primary.main,
          },
        },
      },
    },
  },
  breakpoints: {
    values: {
      xs: 0,
      sm: 600,
      md: 960,
      lg: 1280,
      xl: 1920,
    },
  },
});

// Colori aggiuntivi per uso specifico (se necessario)
export const additionalColors = {
  navbar: fracominaSelloutColors.background.navbar,
  accent: fracominaSelloutColors.primary.light,
  muted: fracominaSelloutColors.text.secondary,
  border: fracominaSelloutColors.divider,
} as const;

export default fracominaSelloutTheme;
